// Estado del período de una pantalla de Reportes Recepción (simple, Pro, Históricas). Los helpers
// puros de fechas viven en fechasReportes.js; acá solo el hook que los combina.
import { useCallback, useMemo, useState } from 'react';
import {
    RANGOS_RAPIDOS, calcularRangoRapido, fechaCorta, hoyISO, recortarRango, fechaISO
} from './fechasReportes';

/**
 * Devuelve el rango ya recortado según el interruptor de turnos futuros como strings
 * (desde/hasta), que son estables para usar como dependencias de los useEffect que piden datos.
 * @param {string} presetInicial id de RANGOS_RAPIDOS (o 'todo' para todo el histórico)
 * @param {Array<{id: string, label: string}>} rangos opciones que ofrece la pantalla
 */
export function usePeriodoReportes(presetInicial = 'mes', rangos = RANGOS_RAPIDOS) {
    const [preset, setPreset] = useState(presetInicial);
    
    // El mes elegido se guarda en formato YYYY-MM
    const [mesElegido, setMesElegido] = useState(() => {
        const hoy = new Date();
        return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
    });
    
    const [dia, setDia] = useState(() => hoyISO());
    const [desdeManual, setDesdeManual] = useState('');
    const [hastaManual, setHastaManual] = useState('');
    const [incluirFuturos, setIncluirFuturos] = useState(false);

    const natural = useMemo(() => {
        if (preset === 'mes' && mesElegido) {
            const [y, m] = mesElegido.split('-');
            const inicio = new Date(parseInt(y), parseInt(m) - 1, 1);
            const fin = new Date(parseInt(y), parseInt(m), 0);
            return { desde: fechaISO(inicio), hasta: fechaISO(fin) };
        }
        if (preset === 'rango') return { desde: desdeManual, hasta: hastaManual };
        return calcularRangoRapido(preset, dia);
    }, [preset, mesElegido, dia, desdeManual, hastaManual]);

    const efectivo = useMemo(() => recortarRango(natural, incluirFuturos), [natural, incluirFuturos]);

    const elegirPreset = useCallback((nuevo) => {
        setPreset(nuevo);
        if (nuevo === 'mes') {
            const hoy = new Date();
            setMesElegido(`${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`);
        }
        if (nuevo === 'rango') {
            // Arranca con el mes en curso para no mostrar una pantalla vacía hasta cargar fechas.
            const mes = calcularRangoRapido('mes');
            setDesdeManual((actual) => actual || mes.desde);
            setHastaManual((actual) => actual || hoyISO());
        }
    }, []);

    const cambiarMes = useCallback((offset) => {
        setMesElegido((prev) => {
            const [y, m] = prev.split('-');
            const date = new Date(parseInt(y), parseInt(m) - 1 + offset, 1);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        });
    }, []);

    const nombreDelMesActual = useMemo(() => {
        if (!mesElegido) return '';
        const [y, m] = mesElegido.split('-');
        const date = new Date(parseInt(y), parseInt(m) - 1, 1);
        return date.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    }, [mesElegido]);

    const etiquetaPreset = rangos.find((r) => r.id === preset)?.label || preset;
    const etiqueta = efectivo.desde || efectivo.hasta
        ? `${fechaCorta(efectivo.desde) || '…'} al ${fechaCorta(efectivo.hasta) || '…'} (${preset === 'mes' ? nombreDelMesActual : etiquetaPreset}${incluirFuturos ? ', con turnos futuros' : ''})`
        : 'Todo el histórico';

    return {
        rangos,
        preset, elegirPreset,
        mesElegido, cambiarMes, nombreDelMesActual,
        dia, setDia,
        desdeManual, setDesdeManual, hastaManual, setHastaManual,
        incluirFuturos, setIncluirFuturos,
        desde: efectivo.desde || '',
        hasta: efectivo.hasta || '',
        recortado: Boolean(efectivo.recortado),
        vacio: Boolean(efectivo.vacio),
        etiqueta,
    };
}

export default usePeriodoReportes;
